import React, { useEffect, useMemo, useState } from "react";
import "../styles/modalAgregarUsuario.css";
import "../styles/institutionNoticeModal.css";
import { NOTICE_PLATFORMS } from "../services/institutionNoticesService";

const MEDIA_BASE_URL = "https://pushnotifications.transformaciondigitalcusco.in/media/";

function toDatetimeLocal(ms) {
  const d = new Date(Number(ms || Date.now()));
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60000);
  return local.toISOString().slice(0, 16);
}

function fromDatetimeLocal(value) {
  const d = new Date(value);
  return d.getTime();
}

function fmtDay(value) {
  const ms = fromDatetimeLocal(value);
  if (!Number.isFinite(ms)) return "--/--/----";
  return new Date(ms).toLocaleDateString("es-PE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function parseHttpsUrl(value) {
  const text = String(value || "").trim();
  if (!text.startsWith("https://")) return null;
  try {
    return new URL(text);
  } catch {
    return null;
  }
}

function isHttpsUrl(value) {
  return !!parseHttpsUrl(value);
}

// https:// y la ruta termina en una de las extensiones (se permiten parámetros como ?v=2)
function isMediaUrl(value, extensions) {
  const url = parseHttpsUrl(value);
  return !!url && extensions.some((ext) => url.pathname.toLowerCase().endsWith(ext));
}

const VIDEO_EXTENSIONS = [".mp4"];
const POSTER_EXTENSIONS = [".jpg", ".png", ".webp"];

const PLATFORM_LABELS = { android: "Android", ios: "iOS", web: "Web" };
const DEFAULT_ACTION_LABELS = { add_widget: "Agregar widget", open_url: "Abrir" };

const emptyForm = {
  title: "",
  body: "",
  level: "info",
  enabled: true,
  startAt: toDatetimeLocal(Date.now()),
  endAt: toDatetimeLocal(Date.now() + 86400000),
  videoUrl: "",
  posterUrl: "",
  action: "",
  actionLabel: "",
  actionUrl: "",
  platforms: [],
};

function NoticePhonePreview({ form, videoSrc, posterSrc, onVideoError }) {
  const paragraphs = form.body.split("\n").filter((p) => p.trim());
  const actionLabel = form.actionLabel.trim() || DEFAULT_ACTION_LABELS[form.action] || "";

  return (
    <div className="notice-phone" aria-label="Vista previa en el celular">
      <div className="notice-phone__screen">
        <div className="notice-phone__header">Notificación</div>
        <div className="notice-phone__media">
          {videoSrc ? (
            <video
              key={videoSrc}
              src={videoSrc}
              poster={posterSrc || undefined}
              controls
              playsInline
              preload="metadata"
              onError={onVideoError}
            />
          ) : (
            <img className="notice-phone__logo" src="/logo192.png" alt="" />
          )}
        </div>
        <div className="notice-phone__content">
          <div className="notice-phone__title">{form.title.trim() || "Título del aviso"}</div>
          {paragraphs.length ? (
            paragraphs.map((p, i) => <p key={i}>{p}</p>)
          ) : (
            <p className="notice-phone__muted">El contenido del aviso aparecerá aquí.</p>
          )}
          <div className="notice-phone__validity">
            Vigencia: {fmtDay(form.startAt)} - {fmtDay(form.endAt)}
          </div>
        </div>
        <div className="notice-phone__actions">
          {form.action ? <span className="notice-phone__btn notice-phone__btn--primary">{actionLabel}</span> : null}
          <span className={`notice-phone__btn ${form.action ? "notice-phone__btn--secondary" : "notice-phone__btn--primary"}`}>
            Entendido
          </span>
        </div>
      </div>
    </div>
  );
}

export default function ModalInstitutionNotice({
  open = false,
  mode = "create",
  initialData = null,
  designationOptions = [],
  submitting = false,
  onClose,
  onConfirm,
}) {
  const isEditMode = mode === "edit";
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState("");
  const [segmentByDesignation, setSegmentByDesignation] = useState(false);
  const [selectedDesignations, setSelectedDesignations] = useState({});
  const [videoLoadFailed, setVideoLoadFailed] = useState(false);

  useEffect(() => {
    if (!open) return;
    const source = initialData || null;

    setForm(
      source
        ? {
            title: String(source?.title || ""),
            body: String(source?.body || ""),
            level: String(source?.level || "info"),
            enabled: Boolean(source?.enabled),
            startAt: toDatetimeLocal(source?.startAt),
            endAt: toDatetimeLocal(source?.endAt),
            videoUrl: String(source?.videoUrl || ""),
            posterUrl: String(source?.posterUrl || ""),
            action: String(source?.action || ""),
            actionLabel: String(source?.actionLabel || ""),
            actionUrl: String(source?.actionUrl || ""),
            platforms: Array.isArray(source?.platforms) ? source.platforms : [],
          }
        : emptyForm
    );
    setError("");

    const designationsFromInitial = Array.isArray(source?.designations)
      ? source.designations
      : source?.designation
        ? [source.designation]
        : [];

    const designationSet = new Set(
      designationsFromInitial.map((x) => String(x || "").trim()).filter(Boolean)
    );
    setSegmentByDesignation(designationSet.size > 0);

    const nextDesignationSelected = {};
    for (const opt of designationOptions) {
      nextDesignationSelected[opt.key] = designationSet.has(String(opt.key));
    }
    setSelectedDesignations(nextDesignationSelected);
  }, [open, initialData, designationOptions]);

  const selectedDesignationKeys = useMemo(
    () => Object.entries(selectedDesignations).filter(([, checked]) => checked).map(([key]) => key),
    [selectedDesignations]
  );

  const videoUrl = form.videoUrl.trim();
  const posterUrl = form.posterUrl.trim();
  const videoUrlValid = isMediaUrl(videoUrl, VIDEO_EXTENSIONS);
  const posterUrlValid = isMediaUrl(posterUrl, POSTER_EXTENSIONS);
  const videoSrc = videoUrlValid ? videoUrl : "";
  const posterSrc = posterUrlValid ? posterUrl : "";

  // Cada URL nueva se vuelve a comprobar en la vista previa
  useEffect(() => {
    setVideoLoadFailed(false);
  }, [videoSrc]);

  const handleActionChange = (action) => {
    setForm((f) => ({
      ...f,
      action,
      platforms: action === "add_widget" ? ["android"] : f.platforms,
    }));
  };

  const togglePlatform = (platform, checked) => {
    setForm((f) => ({
      ...f,
      platforms: checked
        ? NOTICE_PLATFORMS.filter((p) => p === platform || f.platforms.includes(p))
        : f.platforms.filter((p) => p !== platform),
    }));
  };

  const handleSubmit = async ({ notify }) => {
    setError("");

    if (!form.title.trim()) return setError("El titulo es obligatorio.");
    if (!form.body.trim()) return setError("El contenido es obligatorio.");

    const startMs = fromDatetimeLocal(form.startAt);
    const endMs = fromDatetimeLocal(form.endAt);
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
      return setError("Rango de fechas invalido.");
    }
    if (endMs < startMs) return setError("La fecha final no puede ser menor a la inicial.");
    if (segmentByDesignation && selectedDesignationKeys.length === 0) {
      return setError("Selecciona al menos una designacion para segmentar.");
    }
    if (videoUrl && !videoUrlValid) {
      return setError("La URL del video debe empezar con https:// y terminar en .mp4 (puede llevar ?v=2).");
    }
    if (posterUrl && !posterUrlValid) {
      return setError("La URL de la portada debe empezar con https:// y terminar en .jpg, .png o .webp.");
    }
    if (form.action === "open_url" && !isHttpsUrl(form.actionUrl)) {
      return setError("El enlace del botón es obligatorio y debe empezar con https://.");
    }

    await onConfirm?.({
      title: form.title.trim(),
      body: form.body.trim(),
      level: form.level,
      enabled: !!form.enabled,
      startAt: startMs,
      endAt: endMs,
      recipientIds: [],
      designations: segmentByDesignation ? selectedDesignationKeys : [],
      videoUrl,
      posterUrl,
      action: form.action,
      actionLabel: form.action ? form.actionLabel.trim() : "",
      actionUrl: form.action === "open_url" ? form.actionUrl.trim() : "",
      platforms: form.action === "add_widget" ? ["android"] : form.platforms,
    }, { notify: !!notify });
  };

  if (!open) return null;

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true">
      <div className="modal-card notice-modal-card">
        <div className="modal-header">
          <h3>{isEditMode ? "Editar notificacion" : "Crear notificacion"}</h3>
          <button className="icon-btn" onClick={onClose} aria-label="Cerrar" disabled={submitting}>
            x
          </button>
        </div>

        <div className="notice-modal-layout">
        <form
          className="modal-body"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit({ notify: true });
          }}
        >
          <div className="form-row">
            <label>Titulo</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="Comunicado general"
              autoFocus
            />
          </div>

          <div className="form-row">
            <label>Contenido</label>
            <textarea
              value={form.body}
              onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
              placeholder="Mensaje de la notificacion"
              rows={4}
              style={{ resize: "vertical", padding: 12, borderRadius: 8, border: "1px solid #e6e9ef" }}
            />
            <span className="notice-hint">Cada salto de línea se muestra como un párrafo.</span>
          </div>

          <div className="grid-2">
            <div className="form-row">
              <label>Nivel</label>
              <select value={form.level} onChange={(e) => setForm((f) => ({ ...f, level: e.target.value }))}>
                <option value="info">Info</option>
                <option value="success">Success</option>
                <option value="warning">Warning</option>
                <option value="danger">Danger</option>
                {form.level === "critical" ? <option value="critical">Critical (anterior)</option> : null}
              </select>
            </div>
            <div className="form-row">
              <label>Estado</label>
              <select
                value={form.enabled ? "true" : "false"}
                onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.value === "true" }))}
              >
                <option value="true">Activo</option>
                <option value="false">Inactivo</option>
              </select>
            </div>
          </div>

          <div className="grid-2">
            <div className="form-row">
              <label>Inicio</label>
              <input
                type="datetime-local"
                value={form.startAt}
                onChange={(e) => setForm((f) => ({ ...f, startAt: e.target.value }))}
              />
            </div>
            <div className="form-row">
              <label>Fin</label>
              <input
                type="datetime-local"
                value={form.endAt}
                onChange={(e) => setForm((f) => ({ ...f, endAt: e.target.value }))}
              />
            </div>
          </div>

          <fieldset className="notice-section">
            <legend>Video (opcional)</legend>
            <div className="form-row">
              <label htmlFor="notice-video-url">URL del video</label>
              <input
                id="notice-video-url"
                type="url"
                value={form.videoUrl}
                onChange={(e) => setForm((f) => ({ ...f, videoUrl: e.target.value }))}
                placeholder={`${MEDIA_BASE_URL}aviso.mp4`}
              />
              <span className="notice-hint">
                Sube el archivo a la carpeta media del servidor de notificaciones y pega aquí{" "}
                {MEDIA_BASE_URL}&lt;archivo&gt;.mp4. Si reemplazas un video, cambia el nombre o agrega ?v=2.
              </span>
              {videoUrl && !videoUrlValid ? (
                <span className="notice-hint notice-hint--warn">
                  Debe empezar con https:// y terminar en .mp4.
                </span>
              ) : null}
              {videoSrc && videoLoadFailed ? (
                <span className="notice-hint notice-hint--error" role="alert">
                  No se pudo cargar el video desde esa URL
                </span>
              ) : null}
            </div>

            <div className="form-row">
              <label htmlFor="notice-poster-url">URL de la portada (opcional)</label>
              <input
                id="notice-poster-url"
                type="url"
                value={form.posterUrl}
                onChange={(e) => setForm((f) => ({ ...f, posterUrl: e.target.value }))}
                placeholder={`${MEDIA_BASE_URL}aviso.jpg`}
              />
              <span className="notice-hint">Imagen .jpg, .png o .webp que se muestra antes de reproducir.</span>
              {posterUrl && !posterUrlValid ? (
                <span className="notice-hint notice-hint--warn">
                  Debe empezar con https:// y terminar en .jpg, .png o .webp.
                </span>
              ) : null}
            </div>
          </fieldset>

          <fieldset className="notice-section">
            <legend>Botón de acción (opcional)</legend>
            <div className="grid-2">
              <div className="form-row">
                <label>Acción</label>
                <select value={form.action} onChange={(e) => handleActionChange(e.target.value)}>
                  <option value="">Ninguna</option>
                  <option value="add_widget">Agregar widget de asistencia</option>
                  <option value="open_url">Abrir enlace</option>
                </select>
              </div>
              {form.action ? (
                <div className="form-row">
                  <label>Texto del botón</label>
                  <input
                    type="text"
                    value={form.actionLabel}
                    onChange={(e) => setForm((f) => ({ ...f, actionLabel: e.target.value }))}
                    placeholder={DEFAULT_ACTION_LABELS[form.action]}
                  />
                </div>
              ) : null}
            </div>
            {form.action === "open_url" ? (
              <div className="form-row">
                <label>Enlace</label>
                <input
                  type="url"
                  value={form.actionUrl}
                  onChange={(e) => setForm((f) => ({ ...f, actionUrl: e.target.value }))}
                  placeholder="https://…"
                />
              </div>
            ) : null}
            {form.action === "add_widget" ? (
              <span className="notice-hint">El widget solo existe en Android: el aviso se limita a esa plataforma.</span>
            ) : null}
          </fieldset>

          <fieldset className="notice-section">
            <legend>Plataformas</legend>
            <div className="notice-platforms">
              {NOTICE_PLATFORMS.map((p) => (
                <label key={p} className="designation-item">
                  <span className="designation-item__check">
                    <input
                      type="checkbox"
                      checked={form.platforms.includes(p)}
                      disabled={form.action === "add_widget"}
                      onChange={(e) => togglePlatform(p, e.target.checked)}
                    />
                  </span>
                  <span className="designation-item__label">{PLATFORM_LABELS[p]}</span>
                </label>
              ))}
            </div>
            <span className="notice-hint">Sin ninguna marcada, el aviso se muestra en todas.</span>
          </fieldset>

          <div className="form-row">
            <label className="designation-toggle">
              <input
                type="checkbox"
                checked={segmentByDesignation}
                onChange={(e) => setSegmentByDesignation(e.target.checked)}
              />
              Segmentar por designacion
            </label>

            {segmentByDesignation ? (
              <div className="designation-panel">
                <div className="designation-panel__actions">
                  <button
                    type="button"
                    className="btn ghost"
                    onClick={() => {
                      const all = {};
                      for (const d of designationOptions) all[d.key] = true;
                      setSelectedDesignations(all);
                    }}
                    disabled={!designationOptions.length}
                  >
                    Seleccionar todo
                  </button>
                  <button
                    type="button"
                    className="btn ghost"
                    onClick={() => {
                      const none = {};
                      for (const d of designationOptions) none[d.key] = false;
                      setSelectedDesignations(none);
                    }}
                    disabled={!designationOptions.length}
                  >
                    Limpiar
                  </button>
                </div>

                {!designationOptions.length ? (
                  <div className="designation-empty">
                    No hay designaciones disponibles para segmentar.
                  </div>
                ) : (
                  <div className="designation-grid">
                    {designationOptions.map((d) => (
                      <label key={d.key} className="designation-item">
                        <span className="designation-item__check">
                          <input
                            type="checkbox"
                            checked={!!selectedDesignations[d.key]}
                            onChange={(e) =>
                              setSelectedDesignations((prev) => ({
                                ...prev,
                                [d.key]: e.target.checked,
                              }))
                            }
                          />
                        </span>
                        <span className="designation-item__label">{d.label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            ) : null}
          </div>

          {error ? <div className="error">{error}</div> : null}
        </form>

        <aside className="notice-preview-pane">
          <div className="notice-preview-pane__label">Vista previa</div>
          <NoticePhonePreview
            form={form}
            videoSrc={videoSrc}
            posterSrc={posterSrc}
            onVideoError={() => setVideoLoadFailed(true)}
          />
          {videoSrc && videoLoadFailed ? (
            <div className="notice-preview-error" role="alert">
              No se pudo cargar el video desde esa URL
            </div>
          ) : null}
        </aside>
        </div>

        <div className="modal-actions">
          <button className="btn ghost" type="button" onClick={onClose} disabled={submitting}>
            Cancelar
          </button>
          <button
            className="btn ghost"
            type="button"
            onClick={() => handleSubmit({ notify: false })}
            disabled={submitting}
          >
            {submitting ? "Guardando..." : "Guardar"}
          </button>
          <button
            className="btn primary"
            type="button"
            onClick={() => handleSubmit({ notify: true })}
            disabled={submitting}
          >
            {submitting ? "Enviando..." : "Enviar y notificar"}
          </button>
        </div>
      </div>
    </div>
  );
}
